import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchContent = createAsyncThunk(
  "content/fetchContent",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/content", { params });
      return response.data.data?.content ?? [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch content"
      );
    }
  }
);

export const fetchContentByKey = createAsyncThunk(
  "content/fetchContentByKey",
  async (key, { rejectWithValue }) => {
    try {
      const response = await api.get(`/content/${encodeURIComponent(key)}`);
      return response.data.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch content"
      );
    }
  }
);

export const createContent = createAsyncThunk(
  "content/createContent",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/content", data);
      return response.data.data?.content ?? null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create content"
      );
    }
  }
);

export const updateContent = createAsyncThunk(
  "content/updateContent",
  async ({ key, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/content/${encodeURIComponent(key)}`,
        data
      );
      return response.data.data?.content ?? null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update content"
      );
    }
  }
);

export const deleteContent = createAsyncThunk(
  "content/deleteContent",
  async (key, { rejectWithValue }) => {
    try {
      await api.delete(`/content/${encodeURIComponent(key)}`);
      return key;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete content"
      );
    }
  }
);

const contentSlice = createSlice({
  name: "content",
  initialState: {
    content: [],
    currentContent: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentContent: (state) => {
      state.currentContent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContent.fulfilled, (state, action) => {
        state.loading = false;
        state.content = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchContentByKey.fulfilled, (state, action) => {
        state.currentContent = action.payload;
      })
      .addCase(createContent.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.content = [action.payload, ...state.content];
        }
      })
      .addCase(updateContent.fulfilled, (state, action) => {
        if (!action.payload?.key) return;
        const i = state.content.findIndex(
          (c) => c.key === action.payload.key
        );
        if (i !== -1) state.content[i] = action.payload;
        if (state.currentContent?.key === action.payload.key) {
          state.currentContent = action.payload;
        }
      })
      .addCase(deleteContent.fulfilled, (state, action) => {
        state.content = state.content.filter((c) => c.key !== action.payload);
      });
  },
});

export const { clearError, clearCurrentContent } = contentSlice.actions;
export default contentSlice.reducer;
